'use client';

import { useTranslations } from 'i11n';
import { useCallback, useMemo } from 'react';
import { ComboboxCheckbox, ComboboxOption } from 'ui';
import { filterEnumOpts } from '../utils/filterEnumOpts';
import { mapIdsToOptions } from '../utils/mapIdsToOptions';
import { toggleComboboxOption } from '../utils/toggleComboboxOption';

const noop = () => {};

const OptionLabel = ({ label }: ComboboxOption) => <>{label}</>;

export const FilterOptionsList = <T extends ComboboxOption>({
  selectedValues,
  setSelectedValues,
  loadOptions,
  defaultOptions,
  isMulti = true,
  searchable = true,
}: {
  selectedValues: T[];
  setSelectedValues: (selected: T[]) => void;
  loadOptions: (search: string) => Promise<T[]>;
  defaultOptions?: ComboboxOption[];
  isMulti?: boolean;
  searchable?: boolean;
}) => {
  const t = useTranslations('filtersPanel');

  const onSelect = useCallback(
    (option: T) =>
      setSelectedValues(toggleComboboxOption(selectedValues, option, isMulti)),
    [selectedValues, setSelectedValues, isMulti],
  );

  return (
    <ComboboxCheckbox
      value={selectedValues}
      onClose={noop}
      render={OptionLabel}
      placeholder={t('search')}
      emptyStateMessage={t('empty')}
      onSelect={onSelect}
      loadOptions={loadOptions}
      defaultOptions={defaultOptions ?? selectedValues}
      searchable={searchable}
      withEmptySearchTerm
      selectedFirst
    />
  );
};

export const OptionsFilterList = <Value extends string>({
  options,
  value,
  onChange,
  isMulti,
  searchable,
}: {
  options: ComboboxOption<Value>[];
  value: Value[];
  onChange: (values: Value[]) => void;
  isMulti?: boolean;
  searchable?: boolean;
}) => {
  const selectedValues = useMemo(
    () => mapIdsToOptions(value, options),
    [value, options],
  );

  const setSelectedValues = useCallback(
    (selected: ComboboxOption<Value>[]) =>
      onChange(selected.map((option) => option.value)),
    [onChange],
  );

  const loadOptions = useCallback(
    async (search: string) => filterEnumOpts(options, search),
    [options],
  );

  const remountWhenOptionsLoad = options.length;

  return (
    <FilterOptionsList
      key={remountWhenOptionsLoad}
      selectedValues={selectedValues}
      setSelectedValues={setSelectedValues}
      loadOptions={loadOptions}
      defaultOptions={options}
      isMulti={isMulti}
      searchable={searchable}
    />
  );
};
